import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal } = useCart();
  const navigate = useNavigate();

  const handleRemove = (productId, productName) => {
    removeFromCart(productId);
    toast.success(`${productName} removed from cart`);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <ChatWidget />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center">
            <ShoppingBag className="w-20 h-20 text-african-earth mx-auto mb-6 opacity-50" />
            <h2 className="font-playfair text-3xl font-bold text-[#2D2420] mb-4">
              Your Cart is Empty
            </h2>
            <p className="text-african-earth mb-8">Add some products to get started!</p>
            <Link
              to="/products"
              className="inline-block bg-perfect-peach text-white px-8 py-4 rounded-full font-medium hover:bg-[#C57D63] transition-all duration-300"
              data-testid="continue-shopping-button"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navigation />
      <ChatWidget />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <h1 className="font-playfair text-4xl sm:text-5xl font-bold text-[#2D2420] mb-8">
          Your Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl p-6 shadow-soft flex gap-6"
                data-testid={`cart-item-${item.id}`}
              >
                <img
                  src={item.image_url}
                  alt={item.name}
                  className="w-24 h-24 object-cover rounded-xl"
                />
                <div className="flex-1">
                  <h3 className="font-playfair text-xl font-semibold text-[#2D2420] mb-2">
                    {item.name}
                  </h3>
                  <p className="text-perfect-peach font-bold text-lg mb-4">
                    R{item.price.toFixed(2)}
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 rounded-full border border-african-earth text-african-earth hover:bg-african-earth hover:text-white transition-all duration-300"
                        data-testid={`decrease-quantity-${item.id}`}
                      >
                        <Minus className="w-4 h-4 mx-auto" />
                      </button>
                      <span className="text-lg font-medium w-8 text-center" data-testid={`quantity-${item.id}`}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 rounded-full border border-african-earth text-african-earth hover:bg-african-earth hover:text-white transition-all duration-300"
                        data-testid={`increase-quantity-${item.id}`}
                      >
                        <Plus className="w-4 h-4 mx-auto" />
                      </button>
                    </div>
                    <button
                      onClick={() => handleRemove(item.id, item.name)}
                      className="text-red-500 hover:text-red-600 transition-colors duration-300"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-[#2D2420]">
                    R{(item.price * item.quantity).toFixed(2)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-soft sticky top-24">
              <h3 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-6">
                Order Summary
              </h3>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-african-earth">
                  <span>Subtotal</span>
                  <span>R{getCartTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-african-earth">
                  <span>Shipping</span>
                  <span>Calculated at checkout</span>
                </div>
                <div className="border-t border-[#E0D5D0] pt-4">
                  <div className="flex justify-between text-xl font-bold text-[#2D2420]">
                    <span>Total</span>
                    <span className="text-perfect-peach" data-testid="cart-total">R{getCartTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={handleCheckout}
                className="w-full bg-perfect-peach text-white px-8 py-4 rounded-full font-medium text-lg hover:bg-[#C57D63] transition-all duration-300 hover:-translate-y-1 shadow-lg hover:shadow-xl"
                data-testid="proceed-to-checkout-button"
              >
                Proceed to Checkout
              </button>
              <Link
                to="/products"
                className="block text-center text-african-earth hover:text-perfect-peach transition-colors duration-300 mt-4"
                data-testid="continue-shopping-link"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;